<template>
  <Teleport to="body">
    <div
      id="modal-overlay"
      :class="isStatic ? 'static' : ''"
      tabindex="-1"
      @click="() => close(isStatic)"
    >
      <div
        role="dialog"
        ref="modalRef"
        :aria-labelledby="IDREF"
        aria-modal="true"
        tabindex="0"
        :class="isCenter ? 'modal-center' : ''"
        @click.stop
        @keydown.esc="() => close()"
        @keydown.tab="trapFocus"
      >
        <div class="modal-frame">
          <div id="modal-header">
            <h2
              :id="IDREF"
              tabindex="-1"
              ref="focusableElements"
            >{{ title }}</h2>
            <button
              class="btn-close"
              aria-label="Close"
              ref="focusableElements"
              @click.stop.prevent="() => close()"
            ></button>
          </div>
          <div id="modal-body">
            <slot></slot>
          </div>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<script setup>

import { onMounted, onUnmounted, ref } from "vue";

const IDREF = "modal-dialog";
const modalRef = ref(null);

 const props = defineProps({
   title: String,
   isCenter: Boolean,
   isStatic: Boolean,
 });

const emits = defineEmits(["close"]);

const close = (closeDisable = false) => {
  if (closeDisable) return;
  emits("close");
};

// 포커스 가능한 요소를 ref로 배열 관리
const focusableElements = ref([]);

// MutationObserver를 통해 focusableElements 업데이트 (자식 요소 변경사항 있을 경우에만)
let observer = null;
const setupMutationObserver = () => {
  observer = new MutationObserver(() => {
    getFocusableElements();
  });
  observer.observe(modalRef.value, { childList: true, subtree: true });
};

// 모달 내에서 포커스 순회
const trapFocus = (event) => {
  if (focusableElements.value[0] === undefined) {
    getFocusableElements();
  }
  const firstElement = focusableElements.value[0];
  const lastElement = focusableElements.value[focusableElements.value.length - 1];

  // 모달 자기 자신에 포커스가 있을 때의 처리
  if (document.activeElement === modalRef.value) {
    if (event.shiftKey) {
      // Shift + Tab: 마지막 요소로 이동
      event.preventDefault();
      lastElement.focus();
    } else {
      // Tab: 첫 번째 요소로 이동
      event.preventDefault();
      firstElement.focus();
    }
  }

  if (event.shiftKey && document.activeElement === firstElement) {
    // Shift + Tab: 첫 번째 요소에서 마지막 요소로 이동
    event.preventDefault();
    lastElement.focus();
  } else if (!event.shiftKey && document.activeElement === lastElement) {
    // Tab: 마지막 요소에서 첫 번째 요소로 이동
    event.preventDefault();
    firstElement.focus();
  }
};

const getFocusableElements = () => {
  // 포커스 가능한 모든 요소를 조건에 맞게 선택하여 focusableElements 배열에 추가
  const modal = modalRef?.value;
  if (!modal) return;
  focusableElements.value = Array.from(modal.querySelectorAll(`
    a[href],
    area[href],
    input:not([disabled]),
    select:not([disabled]),
    textarea:not([disabled]),
    button:not([disabled]),
    iframe,
    object,
    embed,
    *[tabindex]:not([tabindex="-1"]),
    *[contenteditable]
  `));
};

onMounted(() => {
  getFocusableElements();
  setupMutationObserver(); // 모달이 열릴 때 MutationObserver 설정
  modalRef?.value?.focus();
});

onUnmounted(() => {
  if (observer?.value) {
    observer?.value?.disconnect(); // 모달이 닫힐 때 MutationObserver 해제
  }
});
</script>

<style scoped>
#modal-overlay {
  z-index: 1056;
  position: fixed;

  width: 100dvw;
  height: 100dvh;

  top: 0;
  left: 0;

  background-color: rgba(0, 0, 0, 0.5);
}

[role="dialog"] {
  width: 498px;
  height: 500px;

  position: fixed;
  top: 28px;
  left: 50%;
  transform: translateX(-50%);

  border-radius: 60px;
  background: #F7F7F7;

  gap: 8px;
  box-sizing: border-box;
  overflow: hidden;
}
[role="dialog"].modal-center {
  top: 50%;
  transform: translate(-50%, -50%);
}

.modal-frame {
  width: 450px;
  height: 319px;

  margin: 24px;
  box-sizing: border-box;

  /*background-color: #44403F;*/
  display: flex;
  flex-direction: column;

  /*border: 1px solid #00F;*/
}

#modal-header {
  margin-top: 8px;
  position: relative;
  height: 64px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 8px;
}

#modal-header h2 {
  font-size: 24px;
  font-weight: 700;
  line-height: 100%;
  letter-spacing: -2%;
  color: #44403F;
  margin: 0;
}

.btn-close {
  position: absolute;
  right: 1px;
  cursor: pointer;
  width: 40px;
  height: 40px;
  background: #EA5414 url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 16 16' fill='white'%3e%3cpath d='M.293.293a1 1 0 0 1 1.414 0L8 6.586 14.293.293a1 1 0 1 1 1.414 1.414L9.414 8l6.293 6.293a1 1 0 0 1-1.414 1.414L8 9.414l-6.293 6.293a1 1 0 0 1-1.414-1.414L6.586 8 .293 1.707a1 1 0 0 1 0-1.414z'/%3e%3c/svg%3e") center/1.4em auto no-repeat;
  padding: 0;
  opacity: 1;
  border: none;
  outline: none;
  box-shadow: none;

  border-radius: 50%;
}

.btn-close:focus {
  opacity: 0.8;   
} 

.btn-close:hover {
  opacity: 0.8;
}
</style>
